import { LightDomStyles, OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import componentStyles from './outline-heading-baseline.css.lit';

export const allowedHeadingSizes = [
  '4xl',
  '3xl',
  '2xl',
  'xl',
  'lg',
  'md',
  'sm',
  'xs',
  '2xs',
] as const;
export type AllowedHeadingSizes = (typeof allowedHeadingSizes)[number];

export const allowedHeadingStyles = [
  'normal',
  'bold',
  'extrabold',
  'eyebrow',
] as const;
export type AllowedHeadingStyles = (typeof allowedHeadingStyles)[number];

export const allowedHeadingColors = ['black', 'blue', 'white'] as const;
export type AllowedHeadingColors = (typeof allowedHeadingColors)[number];

export const allowedAccentColors = [
  'blue-zodiac',
  'cornflower_blue',
  'dark_blue',
  'light_blue',
  'green',
  'fun_green',
  'orange',
  'red',
  'dark_red',
  'dark_gray',
  'dove_gray',
  'gray_70',
  'gray_80',
  'gray_90',
  'gray_100',
] as const;
export type AllowedAccentColors = (typeof allowedAccentColors)[number];

/**
 * The Baseline Heading component.
 * @element outline-heading-baseline
 * @slot defaultSlot
 */
@customElement('outline-heading-baseline')
export class OutlineHeadingBaseline extends OutlineElement {
  static styles = [componentStyles];

  lightDomStyles = new LightDomStyles(
    this,
    [componentStyles],
    this.tagName.toLowerCase()
  );

  /**
   * The heading level size to apply. Optional override to default styles for a given level
   */
  @property({ type: String, reflect: true, attribute: 'level-size' })
  levelSize: AllowedHeadingSizes;

  /**
   * The heading level style to apply. Optional override to default styles for a given level
   */
  @property({ type: String, reflect: true, attribute: 'level-style' })
  levelStyle: AllowedHeadingStyles = 'bold';

  /**
   * The heading level color to apply.
   */
  @property({ type: String, reflect: true, attribute: 'level-color' })
  levelColor: AllowedHeadingColors = 'black';

  /**
   * The accent color to apply.
   */
  @property({ type: String, reflect: true, attribute: 'accent-color' })
  accentColor: AllowedAccentColors = 'green';

  /**
   * Adds the green rule to the bottom of the header.
   */
  @property({ type: Boolean, reflect: true, attribute: 'ruled' })
  ruled = false;

  /**
   * Adds a bottom border to the heading.
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-border-bottom' })
  hasBorderBottom = false;

  connectedCallback(): void {
    super.connectedCallback();
    if (this.levelSize === '4xl') {
      this.levelStyle = 'extrabold';
      this.requestUpdate();
    }
  }
  render(): TemplateResult {
    return html`<slot></slot> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-heading-baseline': OutlineHeadingBaseline;
  }
}
